import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DcButton,
  DcDropDown,
  DcInput,
  PixelFactoryContext,
  PixelTable,
} from 'pixel-base'
import styled from 'styled-components'
import {
  getDemoDataBatches,
  installDemoData,
  removeDemoData,
} from 'utils/api-routes-accounts'
import {
  FinanceActionBar,
  FinanceCard,
  FinanceChecklist,
  FinanceEntityDrawer,
  FinanceInlineAlert,
  FinancePageShell,
  FinanceStatusBadge,
} from '../../components'
import { financeColors, financeRadius } from '../../components/tokens'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { DemoSeedBatch } from '../../types/finance'

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`

const ProfileCard = styled.button<{ $active: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active ? financeColors.infoBorder : financeColors.border};
  border-radius: ${financeRadius.md};
  background: ${({ $active }) =>
    $active ? financeColors.infoBg : financeColors.surface};
  padding: 16px;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: 10px;
`

const ProfileTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${financeColors.text};
`

const ProfileDescription = styled.div`
  font-size: 13px;
  color: ${financeColors.textSoft};
  line-height: 1.5;
`

const PROFILE_OPTIONS = [
  {
    value: 'simple_1m',
    label: 'Simple 1 Month',
    description:
      'Single-month walkthrough dataset that keeps finance pages and reports non-empty.',
    includes: [
      'One month of periods and baseline ledger activity',
      'Core partners, expenses, invoices, vendor bills, and payments',
      'A lightweight dataset for validation and smoke testing',
    ],
  },
  {
    value: 'smb_6m',
    label: 'SMB 6 Months',
    description:
      'A richer six-month finance dataset for realistic business workflows and reporting.',
    includes: [
      'Six months of operational finance activity',
      'Customers, vendors, allocations, and reconciliation-ready balances',
      'Broader report coverage with more realistic ledger movement',
    ],
  },
] as const

const defaultForm = {
  profile: 'simple_1m',
  tag: '',
  force_new_batch: false,
}

const DemoData = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const navigate = useNavigate()
  const batches = useFinanceObservable<DemoSeedBatch[]>(() =>
    getDemoDataBatches(),
  )
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState(defaultForm)

  const selectedProfile =
    PROFILE_OPTIONS.find((profile) => profile.value === form.profile) ||
    PROFILE_OPTIONS[0]

  const install = () => {
    installDemoData(form).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Demo data installed')
        setIsOpen(false)
        setForm(defaultForm)
        batches.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to install demo data'),
    })
  }

  const removeBatch = (batch_id: string) => {
    removeDemoData({ batch_id }).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Demo data removed')
        batches.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to remove demo data'),
    })
  }

  return (
    <>
      <FinancePageShell
        title="Demo Data"
        isLoading={batches.loading}
        primaryAction={{
          label: 'Import Demo Data',
          onClick: () => setIsOpen(true),
        }}
      >
        <FinanceInlineAlert
          tone="info"
          title="Profile-based import"
          message="Choose a profile before importing. Each profile seeds a different level of finance data so you can match the environment to the type of testing you want to do."
        />

        <ProfileGrid>
          {PROFILE_OPTIONS.map((profile) => (
            <ProfileCard
              key={profile.value}
              type="button"
              $active={selectedProfile.value === profile.value}
              onClick={() => {
                setForm({ ...form, profile: profile.value })
                setIsOpen(true)
              }}
            >
              <ProfileTitle>{profile.label}</ProfileTitle>
              <ProfileDescription>{profile.description}</ProfileDescription>
              <FinanceChecklist
                items={profile.includes.map((label) => ({
                  label,
                  complete: true,
                }))}
              />
            </ProfileCard>
          ))}
        </ProfileGrid>

        <PixelTable
          columns={[
            { name: 'Batch ID', selector: (row) => row.batch_id },
            { name: 'Profile', selector: (row) => row.profile },
            { name: 'Tag', selector: (row) => row.tag || '--' },
            {
              name: 'Created At',
              selector: (row) => formatFinanceDate(row.created_at),
            },
            {
              name: 'Status',
              selector: (row) => <FinanceStatusBadge status={row.status} />,
            },
            {
              name: 'Actions',
              selector: (row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                  <DcButton
                    size="sm"
                    variant="outline-primary"
                    onClick={() =>
                      navigate(`/finance/demo-data/batches/${row.batch_id}`)
                    }
                  >
                    View
                  </DcButton>
                  <DcButton
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeBatch(row.batch_id)}
                  >
                    Remove
                  </DcButton>
                </div>
              ),
            },
          ]}
          data={batches.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <FinanceEntityDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Import Demo Data"
        width="34%"
        footer={
          <FinanceActionBar align="end">
            <DcButton
              variant="outline-secondary"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </DcButton>
            <DcButton onClick={install}>Import Batch</DcButton>
          </FinanceActionBar>
        }
      >
        <div style={{ display: 'grid', gap: 16 }}>
          <FinanceCard
            title="Choose a profile"
            description="Pick the level of demo finance data you want to import into this tenant."
          >
            <div style={{ display: 'grid', gap: 16 }}>
              <DcDropDown
                label="Profile"
                value={form.profile}
                options={PROFILE_OPTIONS.map((profile) => ({
                  value: profile.value,
                  label: profile.label,
                }))}
                onChange={(e) => setForm({ ...form, profile: e.target.value })}
              />
              <DcInput
                label="Tag"
                value={form.tag}
                placeholder="Optional run label"
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
              />
            </div>
          </FinanceCard>

          <FinanceCard
            title={selectedProfile.label}
            description={selectedProfile.description}
          >
            <FinanceChecklist
              items={selectedProfile.includes.map((label) => ({
                label,
                complete: true,
              }))}
            />
          </FinanceCard>

          <FinanceInlineAlert
            tone="warning"
            title="Import behavior"
            message="Imports add a new demo batch for this tenant. Use a tag if you want the batch to be easy to identify later."
          />
        </div>
      </FinanceEntityDrawer>
    </>
  )
}

export default DemoData
